import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { DateField } from "../../components/ui/DateField";
import { SearchableCombobox } from "../../components/ui/SearchableCombobox";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import {
  createVisitor,
  updateVisitor,
  type Visitor,
  type VisitorCreatePayload,
} from "../../api/visitors";
import { errMsg, todayISO } from "./visitorUi";

export interface EmployeeOption {
  id: string;
  label: string;
}

const EMPTY: VisitorCreatePayload = {
  visitor_name: "",
  visitor_phone: "",
  visitor_email: "",
  company_name: "",
  visitor_count: 1,
  id_proof_type: "",
  id_proof_number: "",
  purpose: "",
  notes: "",
  visit_date: null,
  expected_time: "",
  host_empt_id: null,
};

const ID_PROOF_TYPES = [
  "Aadhaar",
  "PAN",
  "Driving Licence",
  "Passport",
  "Voter ID",
  "Company ID",
  "Other",
];

/**
 * Create / edit a visitor invitation.
 *
 * Only the host field is permission-sensitive: without the Visitors panel at
 * Manage the backend refuses a host other than the caller, so the picker is
 * only rendered for managers and everyone else simply hosts their own guest.
 *
 * Editing is offered only while the visit is still INVITED — once someone has
 * walked through the door the row is an entry record, and the backend refuses
 * to rewrite it (see routers/visitors.py::update_visitor).
 */
export default function VisitorFormDialog({
  open,
  editing,
  purposes,
  employees,
  canManage,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: Visitor | null;
  purposes: string[];
  employees: EmployeeOption[];
  canManage: boolean;
  onClose: () => void;
  onSaved: (visitor: Visitor, created: boolean) => void;
}) {
  const [form, setForm] = useState<VisitorCreatePayload>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        visitor_name: editing.visitor_name || "",
        visitor_phone: editing.visitor_phone || "",
        visitor_email: editing.visitor_email || "",
        company_name: editing.company_name || "",
        visitor_count: editing.visitor_count || 1,
        id_proof_type: editing.id_proof_type || "",
        id_proof_number: editing.id_proof_number || "",
        purpose: editing.purpose || "",
        notes: editing.notes || "",
        visit_date: editing.visit_date,
        expected_time: editing.expected_time || "",
        host_empt_id: editing.host_empt_id,
      });
    } else {
      setForm({ ...EMPTY, visit_date: todayISO() });
    }
  }, [open, editing]);

  const set = <K extends keyof VisitorCreatePayload>(
    key: K,
    value: VisitorCreatePayload[K]
  ) => setForm((f) => ({ ...f, [key]: value }));

  const employeeLabels = useMemo(() => employees.map((e) => e.label), [employees]);
  const hostLabel = useMemo(() => {
    const match = employees.find((e) => e.id === form.host_empt_id);
    return match?.label || "";
  }, [employees, form.host_empt_id]);

  const submit = async () => {
    const name = (form.visitor_name || "").trim();
    if (!name) {
      toast.error("Visitor name is required");
      return;
    }
    if (!form.visit_date) {
      toast.error("Pick the date of the visit");
      return;
    }
    const count = Number(form.visitor_count || 1);
    if (!Number.isFinite(count) || count < 1 || count > 100) {
      toast.error("Number of visitors must be between 1 and 100");
      return;
    }

    const payload: VisitorCreatePayload = {
      ...form,
      visitor_name: name,
      visitor_count: count,
      // Blank strings would overwrite a stored value with "" on edit; the
      // backend treats null as "no value".
      visitor_phone: (form.visitor_phone || "").trim() || null,
      visitor_email: (form.visitor_email || "").trim() || null,
      company_name: (form.company_name || "").trim() || null,
      id_proof_type: (form.id_proof_type || "").trim() || null,
      id_proof_number: (form.id_proof_number || "").trim() || null,
      purpose: (form.purpose || "").trim() || null,
      notes: (form.notes || "").trim() || null,
      expected_time: (form.expected_time || "").trim() || null,
    };

    setSaving(true);
    try {
      const saved = editing
        ? await updateVisitor(editing.id, payload)
        : await createVisitor(payload);
      toast.success(editing ? "Invitation updated" : "Visitor invited");
      onSaved(saved, !editing);
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "Could not save the invitation"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={18} />
            {editing ? "Edit Invitation" : "Create Visitor"}
          </DialogTitle>
          <DialogDescription>
            {editing
              ? "Change the details of a visit that has not happened yet."
              : "Book a visitor and generate their QR pass."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Visitor name *</Label>
            <Input
              value={form.visitor_name}
              onChange={(e) => set("visitor_name", e.target.value)}
              placeholder="Full name as on their ID"
            />
          </div>

          <div>
            <Label>Phone number</Label>
            <Input
              value={form.visitor_phone || ""}
              onChange={(e) => set("visitor_phone", e.target.value)}
              placeholder="10-digit mobile"
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={form.visitor_email || ""}
              onChange={(e) => set("visitor_email", e.target.value)}
              placeholder="visitor@company.com"
            />
          </div>

          <div>
            <Label>Visiting from (company)</Label>
            <Input
              value={form.company_name || ""}
              onChange={(e) => set("company_name", e.target.value)}
              placeholder="Acme Ltd"
            />
          </div>
          <div>
            <Label>Number of visitors</Label>
            {/* showZero is off: 0 visitors is never a real value, so the blank
                field + placeholder is the right affordance (see ui/input). */}
            <Input
              type="number"
              min={1}
              max={100}
              value={form.visitor_count ?? 1}
              onChange={(e) => set("visitor_count", Number(e.target.value))}
              placeholder="1"
            />
          </div>

          <div>
            <Label>Date of visit *</Label>
            <DateField
              value={form.visit_date}
              onChange={(v) => set("visit_date", v)}
              minDate={todayISO()}
            />
          </div>
          <div>
            <Label>Expected arrival</Label>
            <Input
              type="time"
              value={form.expected_time || ""}
              onChange={(e) => set("expected_time", e.target.value)}
            />
          </div>

          <div>
            <Label>Purpose of visit</Label>
            <SearchableCombobox
              value={form.purpose || ""}
              onChange={(v) => set("purpose", v)}
              options={purposes}
              placeholder="Select or type a purpose"
              searchPlaceholder="Search purposes…"
              emptyText="No data found."
              allowCustom
              matchTriggerWidth
              // The picker portals to z-[200]; inside this dialog it would
              // otherwise open behind the overlay.
              contentClassName="z-[1100]"
            />
          </div>

          <div>
            <Label>Host</Label>
            {canManage ? (
              <SearchableCombobox
                value={hostLabel}
                onChange={(label) => {
                  const match = employees.find((e) => e.label === label);
                  set("host_empt_id", match ? match.id : null);
                }}
                options={employeeLabels}
                placeholder="Who are they visiting?"
                searchPlaceholder="Search employees…"
                emptyText="No data found."
                matchTriggerWidth
                contentClassName="z-[1100]"
              />
            ) : (
              <div className="flex h-10 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-600">
                You — visitors you invite are hosted by you
              </div>
            )}
          </div>

          <div>
            <Label>ID proof type</Label>
            <SearchableCombobox
              value={form.id_proof_type || ""}
              onChange={(v) => set("id_proof_type", v)}
              options={ID_PROOF_TYPES}
              placeholder="Optional"
              searchPlaceholder="Search…"
              emptyText="No data found."
              allowCustom
              matchTriggerWidth
              contentClassName="z-[1100]"
            />
          </div>
          <div>
            <Label>ID proof number</Label>
            <Input
              value={form.id_proof_number || ""}
              onChange={(e) => set("id_proof_number", e.target.value)}
              placeholder="Optional"
            />
          </div>

          <div className="md:col-span-2">
            <Label>Notes for reception</Label>
            <Textarea
              rows={3}
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Anything the desk should know — parking, equipment being carried in, escort required…"
            />
          </div>
        </div>

        <DialogFooter>
          <button className="gt-btn is-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="gt-btn is-primary" onClick={submit} disabled={saving}>
            {saving
              ? "Saving…"
              : editing
                ? "Save changes"
                : "Create & generate QR"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
